/**
 * nutmeg-animations.js — v3
 * Nutmeg Apparels Animation Engine
 *
 * FIXES in v3:
 *  - Removed all overflow:visible manipulation on parent elements
 *    (position:fixed doesn't need it — only CSS transform/filter on
 *     ancestor breaks it, not overflow:hidden)
 *  - Panel pins at top: headerH (below sticky site nav)
 *  - outer.height = panelH + maxScroll (eliminates blank at end)
 *  - outerDocTop cached after layout, not recalculated on every scroll
 *
 * Zero dependencies · ~5 KB · Deferred
 */

(function () {
  'use strict';

  /* ============================================================
     1. SCROLL REVEAL
     ============================================================ */
  function initScrollReveal() {
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var els = document.querySelectorAll('[data-nm-animate]');

    if (reducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('nm-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el    = entry.target;
        var delay = parseFloat(el.dataset.nmDelay || 0);
        setTimeout(function () { el.classList.add('nm-visible'); }, delay * 1000);
        io.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ============================================================
     2. PINNED HORIZONTAL SCROLL — "L" SCROLL
  
     HOW POSITION:FIXED WORKS HERE:
     - CSS overflow:hidden on a parent does NOT clip position:fixed
       children (only CSS transform / filter / perspective on a
       parent creates a new fixed containing-block).
     - So we don't need to touch .page-container's overflow at all.
     - We just set position:fixed via JS scroll events.
  
     STATE MACHINE:
       BEFORE  → position:absolute; top:0  (outer above viewport)
       ACTIVE  → position:fixed; top:headerH  (pinned, cards scroll sideways)
       AFTER   → position:absolute; bottom:0  (clean exit, next section rises in)
  
     SIZING:
       panelH     = vh − headerH
       outerH     = panelH + maxScroll
       (NOT vh + maxScroll — that added a full viewport of blank at end)
     ============================================================ */
  function initHorizontalScroll() {
    var outer = document.querySelector('.nm-hscroll-outer');
    if (!outer) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var panel    = outer.querySelector('.nm-hscroll-sticky');
    var track    = outer.querySelector('.nm-hscroll-track');
    var cards    = outer.querySelectorAll('.nm-hcard');
    var progress = outer.querySelector('.nm-hscroll-progress-bar');
    var hint     = outer.querySelector('.nm-hscroll-hint');

    if (!panel || !track || !cards.length) return;

    var LERP    = 0.1;
    var vw, vh, panelH, cardSlot, totalW, maxScroll;

    /* ---- Compute all dimensions ------------------------------ */
    function computeSizes() {
      vw      = window.innerWidth;
      vh      = window.innerHeight;
      
      /* We now pin the section full-screen (top: 0) and hide the sticky header
         during the pinned state. Thus, panel height is the full viewport height. */
      panelH  = vh;

      /* Card widths: 72% on mobile, 28% on desktop */
      var cardVwFactor = vw <= 991 ? 0.72 : 0.28;
      cardSlot = Math.round(vw * cardVwFactor);
      cards.forEach(function (c) { c.style.width = cardSlot + 'px'; });

      /* Track total width = all cards in one row */
      totalW = cardSlot * cards.length;
      track.style.width = totalW + 'px';

      /* Max horizontal travel */
      maxScroll = Math.max(0, totalW - vw);

      /* Panel height fills viewport */
      panel.style.height = panelH + 'px';

      /* Outer height reserves exactly the scroll distance. */
      outer.style.height = (panelH + maxScroll) + 'px';
    }

    /* ---- Lerp state ------------------------------------------ */
    var currentX = 0, targetX = 0, rafId = null;
    function lerp(a, b, t) { return a + (b - a) * t; }

    /* ---- Scroll handler -------------------------------------- */
    function onScroll() {
      var scrollY = window.scrollY || window.pageYOffset;

      /* Compute current outer's document position dynamically to prevent issues with lazy loading */
      var rect = outer.getBoundingClientRect();
      var outerDocTop = rect.top + scrollY;

      /* scrolledInto: how many px past outer's document-top we are */
      var scrolledInto = scrollY - outerDocTop;
      var scrolled = Math.max(0, Math.min(scrolledInto, maxScroll));

      /* ---- STATE: BEFORE ------------------------------------- */
      if (scrolledInto < 0) {
        panel.style.position = 'absolute';
        panel.style.top      = '0';
        panel.style.bottom   = '';
        panel.style.zIndex   = '';
        targetX = 0;
        document.body.classList.remove('nm-scroll-pinned');

      /* ---- STATE: ACTIVE (pinned) ---------------------------- */
      } else if (scrolledInto < maxScroll) {
        panel.style.position = 'fixed';
        panel.style.top      = '0';
        panel.style.bottom   = '';
        panel.style.zIndex   = '50';
        targetX = scrolled;
        document.body.classList.add('nm-scroll-pinned');

      /* ---- STATE: AFTER (section done, next section rises) --- */
      } else {
        panel.style.position = 'absolute';
        panel.style.top      = '';
        panel.style.bottom   = '0';
        panel.style.zIndex   = '';
        targetX = maxScroll;
        document.body.classList.remove('nm-scroll-pinned');
      }

      /* Shared styles (position doesn't change these) */
      panel.style.left  = '0';
      panel.style.right = '0';
      panel.style.width = '100%';

      /* Progress bar */
      if (progress && maxScroll > 0) {
        progress.style.width = (Math.min(100, scrolled / maxScroll * 100)).toFixed(1) + '%';
      }

      /* Hint fade-out */
      if (hint && scrolled > 60) {
        hint.style.opacity = Math.max(0, 1 - (scrolled - 60) / 100).toString();
      }

      if (!rafId) rafId = requestAnimationFrame(tick);
    }

    /* ---- RAF lerp loop --------------------------------------- */
    function tick() {
      rafId    = null;
      currentX = lerp(currentX, targetX, LERP);
      track.style.transform = 'translateX(-' + currentX.toFixed(2) + 'px)';

      /* Apply premium horizontal parallax shift to card images */
      var trackOffset = -currentX;
      cards.forEach(function (card, index) {
        var anchor = card.querySelector('.product-card__image');
        if (!anchor) return;
        
        var cardLeftInTrack = index * cardSlot;
        var cardLeftInViewport = cardLeftInTrack + trackOffset;
        var cardCenter = cardLeftInViewport + cardSlot / 2;
        var viewportCenter = vw / 2;
        var offsetFromCenter = cardCenter - viewportCenter;
        
        /* Shift by 8% of the offset from the center of screen */
        var parallaxX = -offsetFromCenter * 0.08;
        var maxShift = cardSlot * 0.05; /* Matches the 5% CSS bleed */
        parallaxX = Math.max(-maxShift, Math.min(parallaxX, maxShift));
        
        anchor.style.setProperty('--parallax-x', parallaxX.toFixed(1) + 'px');
      });

      if (Math.abs(targetX - currentX) > 0.3) rafId = requestAnimationFrame(tick);
    }

    /* Set initial absolute position so panel sits at top of outer */
    outer.style.position = 'relative';
    panel.style.position = 'absolute';
    panel.style.top      = '0';
    panel.style.left     = '0';
    panel.style.right    = '0';
    panel.style.width    = '100%';

    computeSizes();
    onScroll();

    /* Listen to scroll */
    window.addEventListener('scroll', onScroll, { passive: true });

    /* On resize: re-compute */
    window.addEventListener('resize', function () {
      clearTimeout(window._nmResizeTimer);
      window._nmResizeTimer = setTimeout(function () {
        computeSizes();
        onScroll();
      }, 180);
    }, { passive: true });
  }

  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    initScrollReveal();
    initHorizontalScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
